import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Tooltip,
  Modal,
  Drawer,
  Descriptions,
  Input,
  notification,
  Card,
  Grid,
  Empty,
  Badge,
  Spin,
} from "antd";
import { useLoading } from "../../context/loadingContext";
import dayjs from "dayjs";
import SearchDatePicker from "../../components/search/datePicker";
import {
  FilterOutlined,
  RedoOutlined,
  RollbackOutlined,
  WarningOutlined,
  CalendarOutlined,
  BankOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useAdmin } from "../../context/adminContext";
import moneyFormat from "../../functions/utils/moneyFormat";
import GetFaultyBankTransactionLog from "functions/admin/getFaultyBankTransactionLog";
import GetBankTransactionLog from "functions/admin/getBankTransactionLog";
import { post } from "../../service/api";
import { useParams } from "react-router-dom";
import SearchAdmin from "components/search/searchAdmin";
import { bg_button, bg_shadow } from "constant/css";
import TablePagination from "components/pagination/tablePagination";

const { useBreakpoint } = Grid;

const renderRefundStatus = (row) => {
  const match = row.refundMatch;
  const amount = parseFloat(row.amount) || 0;

  if (amount > 0) {
    if (match?.status === "matched") {
      const tip =
        match.method === "id"
          ? `Буцаалт #${match.matchedRefundId} — утганд BTL#${row.id}# байна`
          : `Буцаалт #${match.matchedRefundId} — ижил данс + дүнээр тааруулсан (эргэлзээтэй)`;
      return (
        <Tooltip title={tip}>
          <Tag color="success" className="text-xs m-0">
            Буцаагдсан
          </Tag>
        </Tooltip>
      );
    }
    return (
      <Tag color="error" className="text-xs m-0">
        Буцааагүй
      </Tag>
    );
  }

  if (amount < 0) {
    const desc = (row.description || "").toLowerCase();
    if (
      /btl#\d+#/i.test(desc) ||
      desc.includes("aldaatai") ||
      desc.includes("butsaav")
    ) {
      if (match?.status === "matched") {
        const tip =
          match.method === "id"
            ? `Орсон #${match.matchedIncomingId} — яг таарсан`
            : `Орсон #${match.matchedIncomingId} — данс+дүнээр тааруулсан`;
        return (
          <Tooltip title={tip}>
            <Tag color="orange" className="text-xs m-0">
              Буцаалт
            </Tag>
          </Tooltip>
        );
      }
      return (
        <Tag color="default" className="text-xs m-0">
          Холбоогүй
        </Tag>
      );
    }
  }

  return (
    <Tag color="default" className="text-xs m-0">
      Гарсан (-)
    </Tag>
  );
};

const canRefund = (row) =>
  parseFloat(row.amount) > 0 && row.refundMatch?.status !== "matched";

const getLinkedTransactionId = (row) => {
  if (row.refundMatch?.status !== "matched") return null;
  const amount = parseFloat(row.amount) || 0;
  if (amount > 0) return row.refundMatch.matchedRefundId;
  if (amount < 0) return row.refundMatch.matchedIncomingId;
  return null;
};

const hasLinkedTransaction = (row) => getLinkedTransactionId(row) != null;

const StatCard = ({ title, value, sub, accent = "#dc2626", icon }) => (
  <Card
    size="small"
    className="rounded-xl border-0 shadow-sm"
    styles={{ body: { padding: "14px 16px" } }}
    style={{ borderTop: `3px solid ${accent}` }}
  >
    <div className="flex items-center gap-3">
      <div
        className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0"
        style={{ background: `${accent}18`, color: accent, fontSize: 20 }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-gray-500 uppercase tracking-wide">
          {title}
        </div>
        <div className="text-lg sm:text-xl font-extrabold truncate" style={{ color: accent }}>
          {value}
        </div>
        {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  </Card>
);

const FaultyBankTransactionLog = () => {
  const { account } = useParams();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { showLoading, hideLoading } = useLoading();
  const { searchFields, setSearchFields, showFilter, setShowFilter } =
    useAdmin();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({});
  const [summary, setSummary] = useState({
    incomingTotal: 0,
    refundedTotal: 0,
    netTotal: 0,
    incomingCount: 0,
    refundedCount: 0,
    unmatchedIncomingCount: 0,
    unmatchedIncomingTotal: 0,
    matchedIncomingCount: 0,
    orphanRefundCount: 0,
  });
  const [refundModal, setRefundModal] = useState({ open: false, record: null });
  const [refundFromAccount, setRefundFromAccount] = useState(account || "");
  const [refundTransferId, setRefundTransferId] = useState("");
  const [linkedDrawer, setLinkedDrawer] = useState({
    open: false,
    source: null,
    linked: null,
    loading: false,
  });

  const getNextTransferId = () => {
    const today = dayjs().format("YYYY-MM-DD");
    const saved = JSON.parse(localStorage.getItem("refund_transferid") || "{}");
    if (saved.date === today) {
      return String(saved.id).padStart(4, "0");
    }
    return "0190";
  };

  const saveAndIncrementTransferId = (currentId) => {
    const today = dayjs().format("YYYY-MM-DD");
    localStorage.setItem(
      "refund_transferid",
      JSON.stringify({ date: today, id: parseInt(currentId, 10) + 1 })
    );
  };

  useEffect(() => {
    setRefundFromAccount(account || "");
  }, [account]);

  useEffect(() => {
    prepareQueryStringAndSearch();
  }, [page, pageSize]);

  useEffect(() => {
    if (page === 1) prepareQueryStringAndSearch();
    else setPage(1);
  }, [searchFields, account]);

  const prepareQueryStringAndSearch = () => {
    const searchFieldsLength = Object.keys(searchFields).length;
    let queryString = "";
    if (searchFieldsLength !== 0) {
      queryString = Object.keys(searchFields)
        .map((key) => {
          if (!searchFields[key]) return;
          if (key.includes("At") || key.includes("Date")) {
            return `${key}=${searchFields[key]
              .map((e, i) => {
                if (i === 0)
                  return dayjs(e).startOf("day").format("YYYY-MM-DD HH:mm");
                return dayjs(e).endOf("day").format("YYYY-MM-DD HH:mm");
              })
              .join("->")}`;
          }
          return `${key}=${encodeURIComponent(searchFields[key])}`;
        })
        .filter(Boolean)
        .join("&");
    }
    fetchTransactionLogs(queryString, page, pageSize);
  };

  const openRefundModal = (row) => {
    setRefundFromAccount(account);
    setRefundTransferId(getNextTransferId());
    setRefundModal({ open: true, record: row });
  };

  const openLinkedView = async (row) => {
    const linkedId = getLinkedTransactionId(row);
    if (!linkedId) return;

    const cached = data.find((r) => r.id === linkedId);
    if (cached) {
      setLinkedDrawer({ open: true, source: row, linked: cached, loading: false });
      return;
    }

    setLinkedDrawer({ open: true, source: row, linked: null, loading: true });
    try {
      const response = await GetBankTransactionLog(
        `accountNumber=${account}&id=${linkedId}&page=1&limit=1`
      );
      const linked = response?.data?.[0] || null;
      if (!linked) {
        notification.warning({ message: `Гүйлгээ #${linkedId} олдсонгүй` });
        setLinkedDrawer({ open: false, source: null, linked: null, loading: false });
        return;
      }
      setLinkedDrawer({ open: true, source: row, linked, loading: false });
    } catch (e) {
      notification.error({
        message: e.response?.data?.message || e.message || "Алдаа гарлаа",
      });
      setLinkedDrawer({ open: false, source: null, linked: null, loading: false });
    }
  };

  const renderLinkedTransactionPanel = (linked, source) => {
    if (!linked) return null;
    const amount = parseFloat(linked.amount) || 0;
    const isIncoming = amount > 0;
    const matchMethod = source?.refundMatch?.method;

    return (
      <Descriptions
        bordered
        column={1}
        size="small"
        labelStyle={{ width: 130, fontSize: 12 }}
        contentStyle={{ fontSize: 12 }}
      >
        <Descriptions.Item label="ID">
          <span className="font-mono font-semibold">#{linked.id}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Төрөл">
          <Tag color={isIncoming ? "cyan" : "orange"} className="text-xs m-0">
            {isIncoming ? "Орсон алдаатай (+)" : "Буцаалт (-)"}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Дүн">
          <span className="font-semibold">{moneyFormat(Math.abs(amount))}₮</span>
        </Descriptions.Item>
        <Descriptions.Item label="Хүлээн авагч">
          <span className="font-mono">{linked.relatedAccount || "-"}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Утга">
          <span className="text-xs break-all">{linked.description || "-"}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Гүйлгээний огноо">
          {linked.trnDate ? dayjs(linked.trnDate).format("YYYY-MM-DD HH:mm") : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Татсан огноо">
          {linked.createdAt ? dayjs(linked.createdAt).format("YYYY-MM-DD HH:mm") : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Ш/дугаар">
          <span className="font-mono">{linked.journalNo || "-"}</span>
        </Descriptions.Item>
        {matchMethod && (
          <Descriptions.Item label="Холбоо">
            {matchMethod === "id" ? (
              <Tag color="success" className="text-xs m-0">
                Яг таарсан (BTL#id#)
              </Tag>
            ) : (
              <Tag color="warning" className="text-xs m-0">
                Данс + дүнээр тааруулсан — шалгах
              </Tag>
            )}
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  const defaultColumns = [
    {
      title: (
        <SearchAdmin
          sort={false}
          title="ID"
          fieldName="id"
          width={120}
          debounceMs={1500}
          placeholder="ID..."
        />
      ),
      dataIndex: "id",
      key: "id",
      fixed: isMobile ? undefined : "left",
      width: 120,
      render: (v) => <span className="text-xs text-gray-500">{v}</span>,
    },
    {
      title: <span className="text-xs">Данс</span>,
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: 110,
      responsive: ["md"],
      render: (v) => (
        <span className="text-xs font-mono text-gray-700">{v}</span>
      ),
    },
    {
      title: (
        <SearchAdmin
          sort={false}
          title="Хүлээн авагч"
          fieldName="relatedAccount"
          width={160}
          debounceMs={1500}
          placeholder="Данс..."
        />
      ),
      dataIndex: "relatedAccount",
      key: "relatedAccount",
      width: 170,
      render: (v) => (
        <span className="text-xs font-mono text-gray-700">{v || "-"}</span>
      ),
    },
    {
      title: (
        <SearchAdmin
          sort={false}
          title="Гүйлгээний утга"
          fieldName="description"
          width={300}
          debounceMs={500}
          placeholder="Утга хайх..."
        />
      ),
      dataIndex: "description",
      key: "description",
      width: 300,
      ellipsis: true,
      render: (e, row) => {
        const text = String(e || "").substring(0, 36);
        const isIncoming = parseFloat(row.amount) > 0;
        return (
          <div className="flex items-center gap-2 min-w-0">
            <Tooltip title={e}>
              <Tag
                color="error"
                className="text-xs m-0 max-w-full truncate"
                style={{ maxWidth: "100%" }}
              >
                {text}
              </Tag>
            </Tooltip>
            {canRefund(row) && (
              <Button
                size="small"
                danger
                type="primary"
                icon={<RollbackOutlined />}
                onClick={() => openRefundModal(row)}
              />
            )}
          </div>
        );
      },
    },
    {
      title: <span className="text-xs">Төлөв</span>,
      key: "refundStatus",
      width: 120,
      render: (_, row) => renderRefundStatus(row),
    },
    {
      title: <span className="text-xs">Дүн</span>,
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 120,
      render: (e) => {
        const amount = parseFloat(e) || 0;
        const isIncoming = amount > 0;
        return (
          <div className="flex flex-col items-end gap-0.5">
            <Tag
              color={isIncoming ? "cyan" : "warning"}
              className="text-xs font-semibold m-0"
            >
              {moneyFormat(Math.abs(amount))}₮
            </Tag>
            <span
              className="text-[10px] leading-none"
              style={{ color: isIncoming ? "#0891b2" : "#d97706" }}
            >
              {isIncoming ? "Орсон (+)" : "Буцаасан (-)"}
            </span>
          </div>
        );
      },
    },
    {
      title: <span className="text-xs">Үлдэгдэл</span>,
      dataIndex: "remain",
      key: "remain",
      align: "right",
      width: 130,
      responsive: ["lg"],
      render: (e) => (
        <span className="text-xs text-gray-600">
          {moneyFormat(parseFloat(e))}₮
        </span>
      ),
    },
    {
      title: <span className="text-xs">Гүйлгээний огноо</span>,
      dataIndex: "trnDate",
      key: "trnDate",
      width: 110,
      responsive: ["xl"],
      render: (e) => (
        <span className="text-xs text-gray-500">
          {e ? dayjs(e).format("YYYY-MM-DD") : "-"}
        </span>
      ),
    },
    {
      title: (
        <SearchDatePicker
          title="Татсан огноо"
          datePicker
          fieldName="createdAt"
          width={280}
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 290,
      render: (e) => (
        <span className="text-xs text-gray-500">
          {e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "-"}
        </span>
      ),
    },
    {
      title: <span className="text-xs">Холбоо</span>,
      key: "linked",
      fixed: "right",
      width: 90,
      render: (_, row) =>
        hasLinkedTransaction(row) ? (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            className="text-xs px-0"
            onClick={() => openLinkedView(row)}
          >
            Харах
          </Button>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        ),
    },
  ];

  const fetchTransactionLogs = async (queryString, page, size) => {
    showLoading();
    const response = await GetFaultyBankTransactionLog(
      `&accountNumber=${account}&page=${page}&size=${size}&${queryString}`
    );
    hideLoading();
    if (response) {
      setData(response.data);
      setPaginationData(response.pagination);
      setSummary(
        response.summary || {
          incomingTotal: 0,
          refundedTotal: 0,
          netTotal: 0,
          incomingCount: 0,
          refundedCount: 0,
          unmatchedIncomingCount: 0,
          unmatchedIncomingTotal: 0,
          matchedIncomingCount: 0,
          orphanRefundCount: 0,
        }
      );
    }
  };

  const changePage = (page) => {
    setPage(page);
  };

  const changeSize = (current, size) => {
    setPageSize(size);
  };

  const refresh = () => {
    setPage(1);
    setSearchFields({});
    fetchTransactionLogs("", 1, pageSize);
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleRefundSubmit = async () => {
    const rec = refundModal.record;
    showLoading();
    try {
      const payload = {
        fromAccount: refundFromAccount,
        toAccount: rec.relatedAccount,
        amount: Math.abs(parseFloat(rec.amount)),
        refundTransferId: refundTransferId,
        bankLogId: rec.id,
      };

      await post("/admin/transfer/domestic", payload);

      saveAndIncrementTransferId(refundTransferId);
      setRefundModal({ open: false, record: null });
      notification.success({ message: "Гүйлгээ амжилттай буцаагдлаа" });
    } catch (err) {
      notification.error({
        message: err.response?.data?.message || err.message || "Алдаа гарлаа",
      });
    } finally {
      hideLoading();
    }
  };

  const totalCount = paginationData?.total ?? data.length;
  const isBalanced = Math.abs(summary.netTotal) < 0.01;

  const renderAmountSummary = () => {
    if (!totalCount) return null;

    return (
      <div
        className="border-t-2 border-red-200 rounded-b-xl overflow-hidden"
        style={{ background: "linear-gradient(180deg, #fef2f2 0%, #fff 100%)" }}
      >
        <div className="px-3 sm:px-4 py-2 border-b border-red-100">
          <span className="text-xs font-semibold text-gray-500">
            Хөл дүн — бүх шүүлтийн нийлбэр
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-red-100">
          <div className="px-4 py-3 flex flex-col sm:items-center sm:text-center">
            <span className="text-[11px] text-gray-500 mb-1">
              Орсон алдаатай (+)
            </span>
            <span className="text-lg font-extrabold" style={{ color: "#0891b2" }}>
              {moneyFormat(summary.incomingTotal)}₮
            </span>
            <span className="text-[10px] text-gray-400 mt-0.5">
              {summary.incomingCount} гүйлгээ — дансанд орсон
            </span>
          </div>

          <div className="px-4 py-3 flex flex-col sm:items-center sm:text-center">
            <span className="text-[11px] text-gray-500 mb-1">Буцаасан (-)</span>
            <span className="text-lg font-extrabold" style={{ color: "#d97706" }}>
              {moneyFormat(summary.refundedTotal)}₮
            </span>
            <span className="text-[10px] text-gray-400 mt-0.5">
              {summary.refundedCount} гүйлгээ — буцааж шилжүүлсэн
            </span>
          </div>

          <div className="px-4 py-3 flex flex-col sm:items-center sm:text-center">
            <span className="text-[11px] text-gray-500 mb-1">Буцааагүй үлдсэн</span>
            <span className="text-lg font-extrabold" style={{ color: "#dc2626" }}>
              {moneyFormat(summary.unmatchedIncomingTotal || 0)}₮
            </span>
            <span className="text-[10px] text-gray-400 mt-0.5">
              {summary.unmatchedIncomingCount || 0} гүйлгээ — буцаах шаардлагатай
            </span>
          </div>

          <div className="px-4 py-3 flex flex-col sm:items-center sm:text-center">
            <span className="text-[11px] text-gray-500 mb-1">Зөрүү</span>
            <span
              className="text-lg font-extrabold"
              style={{ color: isBalanced ? "#16a34a" : "#dc2626" }}
            >
              {moneyFormat(summary.netTotal)}₮
            </span>
            <span className="text-[10px] text-gray-400 mt-0.5">
              {summary.matchedIncomingCount || 0} хослол таарсан
              {(summary.orphanRefundCount || 0) > 0 &&
                ` · ${summary.orphanRefundCount} холбоогүй буцаалт`}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileList = () => {
    if (!data.length) {
      return (
        <div className="py-12">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="text-gray-500 text-sm">
                Алдаатай гүйлгээ олдсонгүй
              </span>
            }
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {data.map((row) => {
          const amount = parseFloat(row.amount) || 0;
          const isIncoming = amount > 0;
          return (
            <Card
              key={row.id}
              size="small"
              className="rounded-xl border border-red-100 shadow-sm overflow-hidden"
              styles={{ body: { padding: 0 } }}
            >
              <div
                className="px-3 py-2 flex items-center justify-between gap-2"
                style={{ background: "linear-gradient(90deg, #fef2f2 0%, #fff7ed 100%)" }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Badge count={row.id} color="#dc2626" overflowCount={999999} />
                  <span className="text-[10px] font-mono text-gray-500 truncate">
                    {row.relatedAccount || "-"}
                  </span>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="text-base font-extrabold"
                    style={{ color: isIncoming ? "#0891b2" : "#d97706" }}
                  >
                    {moneyFormat(Math.abs(amount))}₮
                  </div>
                  <div
                    className="text-[10px] font-semibold"
                    style={{ color: isIncoming ? "#0891b2" : "#d97706" }}
                  >
                    {isIncoming ? "Орсон (+)" : "Буцаасан (-)"}
                  </div>
                </div>
              </div>

              <div className="px-3 py-2.5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  {renderRefundStatus(row)}
                  {hasLinkedTransaction(row) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<LinkOutlined />}
                      className="text-xs px-1 h-auto"
                      onClick={() => openLinkedView(row)}
                    >
                      Холбоо
                    </Button>
                  )}
                </div>
                <Tooltip title={row.description}>
                  <Tag color="error" className="text-xs m-0 w-full truncate block">
                    {row.description || "-"}
                  </Tag>
                </Tooltip>

                <div className="flex flex-wrap items-center gap-2 mt-2.5 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <CalendarOutlined />
                    {row.trnDate
                      ? dayjs(row.trnDate).format("YYYY-MM-DD")
                      : "-"}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    Татсан:{" "}
                    {row.createdAt
                      ? dayjs(row.createdAt).format("MM-DD HH:mm")
                      : "-"}
                  </span>
                  {row.remain != null && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span>Үлд: {moneyFormat(parseFloat(row.remain))}₮</span>
                    </>
                  )}
                </div>
              </div>

              {canRefund(row) && (
                <div className="px-3 py-2 border-t border-red-50 bg-gray-50/60">
                  <Button
                    danger
                    type="primary"
                    block
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => openRefundModal(row)}
                    className="font-semibold"
                  >
                    Гүйлгээ буцаах
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="px-1 sm:px-0 pb-2">
      {/* Header */}
      <div
        className="rounded-xl p-4 sm:p-5 mb-4"
        style={{
          background: "linear-gradient(135deg, #fef2f2 0%, #fff7ed 50%, #f0fdfa 100%)",
          border: "1px solid #fecaca",
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <WarningOutlined style={{ color: "#dc2626", fontSize: 20 }} />
              <h2 className="text-base sm:text-lg font-extrabold text-red-700 m-0">
                Алдаатай гүйлгээ
              </h2>
            </div>
            <p className="text-xs text-gray-600 m-0 leading-relaxed">
              Орсон (+) гүйлгээг буцаасан (-)-тай холбож төлөв харуулна.
              Хуучин буцаалтууд данс + дүнээр тааруулна; шинэ буцаалтын утга:{" "}
              <span className="font-mono">BTL#id# aldaatai guilgee butsaav</span>
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <BankOutlined className="text-gray-400 text-xs" />
              <span className="text-xs font-mono font-semibold text-gray-700">
                {account}
              </span>
            </div>
          </div>

          <div className="flex gap-2 w-full sm:w-auto shrink-0">
            <Button
              icon={<RedoOutlined />}
              onClick={refresh}
              className={`${bg_button} text-white border-0 flex-1 sm:flex-none rounded-lg ${bg_shadow}`}
            >
              {!isMobile && "Шинэчлэх"}
            </Button>
            <Button
              icon={<FilterOutlined />}
              onClick={toggleFilter}
              className={`flex-1 sm:flex-none rounded-lg border-0 ${
                showFilter
                  ? `${bg_button} text-white ${bg_shadow}`
                  : "bg-white text-gray-600 shadow-sm"
              }`}
            >
              {!isMobile && "Шүүлт"}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <StatCard
          title="Буцааагүй"
          value={`${summary.unmatchedIncomingCount || 0}`}
          sub={`${moneyFormat(summary.unmatchedIncomingTotal || 0)}₮ үлдсэн`}
          accent="#dc2626"
          icon={<WarningOutlined />}
        />
        <StatCard
          title="Буцаагдсан"
          value={`${summary.matchedIncomingCount || 0}`}
          sub="Орсон гүйлгээ хослогдсон"
          accent="#16a34a"
          icon={<RollbackOutlined />}
        />
        <StatCard
          title="Орсон (+)"
          value={`${moneyFormat(summary.incomingTotal)}₮`}
          sub={`${summary.incomingCount} гүйлгээ`}
          accent="#0891b2"
          icon={<BankOutlined />}
        />
        <StatCard
          title="Буцаасан (-)"
          value={`${moneyFormat(summary.refundedTotal)}₮`}
          sub={`${summary.refundedCount} гүйлгээ`}
          accent="#d97706"
          icon={<RollbackOutlined />}
        />
        <StatCard
          title="Зөрүү"
          value={`${moneyFormat(summary.netTotal)}₮`}
          sub={isBalanced ? "Тэнцсэн ✓" : "Орсон − Буцаасан"}
          accent={isBalanced ? "#16a34a" : "#dc2626"}
          icon={<WarningOutlined />}
        />
      </div>

      {/* Table / Mobile list */}
      <div
        className={`bg-white rounded-xl overflow-hidden ${
          isMobile ? "p-3 shadow-sm border border-gray-100" : "shadow-sm border border-gray-100"
        }`}
      >
        {isMobile ? (
          renderMobileList()
        ) : (
          <Table
            columns={defaultColumns}
            rowKey={(record) => record.id}
            scroll={{ x: "max-content", y: "calc(100vh - 420px)" }}
            size="small"
            pagination={false}
            dataSource={data}
            rowClassName={() => "hover:bg-red-50/40 transition-colors"}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Алдаатай гүйлгээ олдсонгүй"
                />
              ),
            }}
          />
        )}

        {renderAmountSummary()}

        <div className="px-2 sm:px-3 pb-2 pt-2">
          <TablePagination
            paginationData={paginationData}
            page={page}
            changePage={changePage}
            changeSize={changeSize}
          />
        </div>
      </div>

      <Modal
        title={
          <span className="text-red-600 font-semibold">
            <RollbackOutlined className="mr-2" />
            Гүйлгээ буцаах
          </span>
        }
        open={refundModal.open}
        onCancel={() => setRefundModal({ open: false, record: null })}
        footer={
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button onClick={() => setRefundModal({ open: false, record: null })}>
              Цуцлах
            </Button>
            <Button danger type="primary" onClick={handleRefundSubmit}>
              Буцаах
            </Button>
          </div>
        }
        width={isMobile ? "100%" : 560}
        style={isMobile ? { top: 20, maxWidth: "calc(100vw - 32px)" } : undefined}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      >
        {refundModal.record &&
          (() => {
            const rec = refundModal.record;
            const amount = Math.abs(parseFloat(rec.amount));
            return (
              <Descriptions
                bordered
                column={1}
                size="small"
                labelStyle={{ width: isMobile ? 110 : 140, fontSize: 12 }}
                contentStyle={{ fontSize: 12 }}
              >
                <Descriptions.Item label="fromAccount">
                  <Input
                    value={refundFromAccount}
                    onChange={(e) => setRefundFromAccount(e.target.value)}
                    size="small"
                  />
                </Descriptions.Item>
                <Descriptions.Item label="toAccount">
                  <span className="font-mono">{rec.relatedAccount}</span>
                </Descriptions.Item>
                <Descriptions.Item label="toCurrency">MNT</Descriptions.Item>
                <Descriptions.Item label="amount">
                  <Tag color="warning" className="font-semibold m-0">
                    {moneyFormat(amount)}₮
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="description">
                  <span className="font-mono text-xs break-all">
                    {`BTL#${rec.id}# aldaatai guilgee butsaav`}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="currency">MNT</Descriptions.Item>
                <Descriptions.Item label="loginName">
                  contact@smartbilling.mn
                </Descriptions.Item>
                <Descriptions.Item label="tranPassword">
                  @BillinG!@#321
                </Descriptions.Item>
                <Descriptions.Item label="transferid">
                  <Input
                    value={refundTransferId}
                    onChange={(e) => setRefundTransferId(e.target.value)}
                    size="small"
                  />
                </Descriptions.Item>
                <Descriptions.Item label="URL">domestic</Descriptions.Item>
              </Descriptions>
            );
          })()}
      </Modal>

      <Drawer
        title={
          linkedDrawer.source &&
          (parseFloat(linkedDrawer.source.amount) > 0
            ? `Буцаалтын гүйлгээ — #${getLinkedTransactionId(linkedDrawer.source)}`
            : `Анх орсон гүйлгээ — #${getLinkedTransactionId(linkedDrawer.source)}`)
        }
        placement="right"
        width={isMobile ? "100%" : 420}
        open={linkedDrawer.open}
        onClose={() =>
          setLinkedDrawer({ open: false, source: null, linked: null, loading: false })
        }
      >
        {linkedDrawer.loading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : (
          renderLinkedTransactionPanel(linkedDrawer.linked, linkedDrawer.source)
        )}
      </Drawer>
    </div>
  );
};

export default FaultyBankTransactionLog;
